import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@repo/api-sdk';
import { PageLoader } from '@repo/ui';
import { useTranslation } from 'react-i18next';
import {
  LayoutDashboard,
  Users2,
  ClipboardCheck,
  TicketCheck,
  CalendarClock,
  Clock,
  Video,
  Wifi,
  WifiOff,
  BookOpen,
  Zap,
  ExternalLink,
  Calendar,
  ArrowUpRight,
  User,
  MoreVertical,
  MapPin,
  QrCode,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import QRScannerModal from '../components/qr/QRScannerModal';

// Detect mobile browser
const isMobileDevice = () =>
  typeof window !== 'undefined' &&
  (/Android|iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    ('ontouchstart' in window && window.innerWidth < 1024));

// ── helpers ──────────────────────────────────────────────────────────────────

function formatTime(t: string | Date | undefined, lang: string) {
  if (!t) return null;

  let date: Date;
  if (typeof t === 'string' && t.includes(':')) {
    const [hStr, mStr] = t.split(':');
    date = new Date();
    date.setHours(parseInt(hStr, 10), parseInt(mStr, 10), 0, 0);
  } else {
    date = new Date(t);
  }

  const h = date.getHours();
  const m = date.getMinutes();

  if (lang === 'ar') {
    const ampm = h >= 12 ? 'م' : 'ص';
    const h12 = h % 12 || 12;
    return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
  }

  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = h % 12 || 12;
  return `${h12}:${m.toString().padStart(2, '0')} ${ampm}`;
}

function formatSessionDate(dateStr: string | undefined, lang: string) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });
}

// ── component ─────────────────────────────────────────────────────────────────

const DashboardPage = () => {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const navigate = useNavigate();
  const [showScanner, setShowScanner] = useState(false);
  const isMobile = isMobileDevice();

  const { data, isLoading } = useQuery({
    queryKey: ['student-dashboard'],
    queryFn: () => api.get('/student/dashboard').then((res) => res.data?.data),
    refetchInterval: 30_000,
  });

  if (isLoading) {
    return (
      <PageLoader
        message={t('student.dashboard.loading', { defaultValue: 'Preparing dashboard...' })}
      />
    );
  }

  const activeSessions: any[] = data?.activeSessions ?? [];

  // Support both upcomingSessions (array, new) and upcomingSession (single, legacy)
  const upcomingSessions: any[] = data?.upcomingSessions?.length
    ? data.upcomingSessions
    : data?.upcomingSession
      ? [data.upcomingSession]
      : [];

  const stats = [
    {
      label: t('student.dashboard.enrolledGroups'),
      value: data?.enrolledGroups ?? 0,
      icon: Users2,
      iconColor: 'text-indigo-600 dark:text-indigo-400',
      iconBg: 'bg-indigo-500/10',
      accentBar: 'bg-indigo-500',
      valueSuffix: '',
      path: '/groups',
    },
    {
      label: t('student.dashboard.attendanceRate'),
      value: data?.attendanceSummary?.rate ?? data?.attendanceRate ?? 0,
      icon: ClipboardCheck,
      iconColor: 'text-emerald-600 dark:text-emerald-400',
      iconBg: 'bg-emerald-500/10',
      accentBar: 'bg-emerald-500',
      valueSuffix: '%',
      path: '/attendance',
    },
    {
      label: t('student.dashboard.openTickets'),
      value: data?.openTickets ?? 0,
      icon: TicketCheck,
      iconColor: 'text-amber-600 dark:text-amber-400',
      iconBg: 'bg-amber-500/10',
      accentBar: 'bg-amber-500',
      valueSuffix: '',
      path: '/tickets',
    },
  ];

  const getSessionTimes = (session: any) => {
    const st = session.scheduledTime;
    if (st?.scheduledStartDate) {
      const start = formatTime(new Date(st.scheduledStartDate), i18n.language);
      const end = st.scheduledEndDate
        ? formatTime(new Date(st.scheduledEndDate), i18n.language)
        : null;
      return { start, end };
    }

    // Fallback 1: if startDate is provided (common in upcoming Sessions API)
    if (session.startDate) {
      const startObj = new Date(session.startDate);
      const start = startObj.toLocaleTimeString(i18n.language === 'ar' ? 'ar-EG' : 'en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });

      let end = null;
      if (session.endDate) {
        end = new Date(session.endDate).toLocaleTimeString(
          i18n.language === 'ar' ? 'ar-EG' : 'en-US',
          { hour: '2-digit', minute: '2-digit' }
        );
      } else if (session.scheduledTime?.scheduledEndDate) {
        end = new Date(session.scheduledTime.scheduledEndDate).toLocaleTimeString(
          i18n.language === 'ar' ? 'ar-EG' : 'en-US',
          { hour: '2-digit', minute: '2-digit' }
        );
      }
      return { start, end };
    }

    // Fallback 2: for sessions that provide 'startTime' / 'endTime' directly
    if (session.startTime) {
      const start = formatTime(session.startTime, i18n.language);
      const end = session.endTime ? formatTime(session.endTime, i18n.language) : null;
      return { start, end };
    }

    // Fallback 3: check if there's a standalone sessionTime
    if (session.sessionTime) {
      const start = formatTime(session.sessionTime, i18n.language);
      return { start, end: null };
    }

    // Fallback 4: if sessionDate holds the time component
    if (session.sessionDate) {
      const dateObj = new Date(session.sessionDate);
      if (dateObj.getUTCHours() !== 0 || dateObj.getUTCMinutes() !== 0) {
        const start = formatTime(dateObj, i18n.language);
        // Note: we might not have a distinct end time in this format unless explicitly provided
        return { start, end: null };
      }
    }

    return { start: null, end: null };
  };

  return (
    <div className="space-y-4 pb-6 overflow-x-hidden">
      {/* ── QR Scanner Modal ── */}
      {showScanner && <QRScannerModal onClose={() => setShowScanner(false)} />}

      {/* ── Fixed QR FAB — Mobile only, bottom-center ── */}
      {isMobile && (
        <div className="fixed bottom-[24px] left-1/2 -translate-x-1/2 z-40 flex flex-col items-center gap-1.5">
          <button
            onClick={() => setShowScanner(true)}
            className="flex items-center justify-center w-[58px] h-[58px] rounded-full bg-primary hover:bg-primary/90 active:scale-95 transition-all shadow-[0_8px_24px_-4px_rgba(var(--primary-rgb),0.6)] border-[3px] border-white/30"
            style={{
              animation: 'qrpulse 1.8s ease-in-out infinite',
              boxShadow: '0 8px 24px -4px hsla(var(--primary), 0.6)',
            }}
          >
            <div className="flex items-center justify-center w-[42px] h-[42px] rounded-full bg-white/15">
              <QrCode className="w-6 h-6 text-white" strokeWidth={2} />
            </div>
          </button>
          <span className="text-[10px] font-black text-primary leading-none drop-shadow-sm">
            {t('student.attendance.checkIn', { defaultValue: 'سجل حضوري' })}
          </span>
        </div>
      )}

      {/* ── Header ── */}
      <div className="flex items-center justify-between">
        <div className="flex flex-col gap-1">
          <h1 className="text-[22px] font-black tracking-tight text-foreground flex items-center gap-2">
            <div className="flex items-center justify-center w-[30px] h-[30px] rounded-[10px] bg-primary/10 border border-primary/20">
              <LayoutDashboard className="w-4 h-4 text-primary" strokeWidth={2.5} />
            </div>
            {t('student.dashboard.title')}
          </h1>
          <p className="text-[11px] font-bold text-muted-foreground/70 mis-[38px]">
            {t('student.auth.heroSubtitle')}
          </p>
        </div>
      </div>

      {/* ── Stats Grid ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {stats.map((stat, idx) => (
          <button
            key={idx}
            onClick={() => navigate(stat.path)}
            className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 text-start transition-all duration-300 cursor-pointer hover:shadow-lg hover:-translate-y-1 hover:border-primary/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
          >
            {/* Top row: Label and Icon */}
            <div className="flex items-center justify-between mb-4 relative z-10">
              <p className="text-xs font-bold text-muted-foreground   ">{stat.label}</p>
              <div
                className={`shrink-0 h-10 w-10 rounded-xl ${stat.iconBg} flex items-center justify-center transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6`}
              >
                <stat.icon className={`h-5 w-5 ${stat.iconColor}`} />
              </div>
            </div>

            {/* Bottom row: Value and Arrow */}
            <div className="flex items-end justify-between relative z-10">
              <p className="text-4xl font-black tracking-tight leading-none text-foreground">
                {stat.value}
                {stat.valueSuffix}
              </p>

              <div className="h-8 w-8 rounded-full bg-muted/50 flex items-center justify-center transition-colors group-hover:bg-primary/10">
                <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors duration-200" />
              </div>
            </div>

            {/* Subtle background glow in bottom right */}
            <div
              className={`absolute -bottom-8 -right-8 h-32 w-32 rounded-full ${stat.accentBar} opacity-10 blur-2xl group-hover:opacity-20 transition-opacity pointer-events-none`}
            />
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
        {/* ── Current/Ongoing Sessions ──────────────────────────────────── */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center justify-between px-1 mb-2">
            <div className="flex items-center gap-2.5">
              {/* Clean Tailwind Ping Indicator */}
              <div className="relative flex h-2.5 w-2.5 items-center justify-center">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-500 opacity-75"></span>
                <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-emerald-500"></span>
              </div>
              <h2 className="text-base font-black tracking-tight text-foreground">
                {t('student.dashboard.todaySessions')}
              </h2>
            </div>
            {activeSessions.length > 0 && (
              <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-black uppercase   text-emerald-600 dark:text-emerald-400 border border-emerald-500/20">
                {activeSessions.length} {t('common.active', { defaultValue: 'Active' })}
              </span>
            )}
          </div>

          <div className="space-y-5">
            {activeSessions.length > 0 ? (
              activeSessions.map((session: any) => {
                const isOngoing =
                  session.isOngoing || session.status === 'ongoing' || session.status === 'started';
                const isOnline = session.deliveryMode === 'online';
                const hasJoinUrl = isOnline && !!session.joinUrl;
                const groupNames =
                  session.groups?.length > 0
                    ? session.groups.map((g: any) => g.name).join(', ')
                    : session.sessionFormula === 'workshop-exception'
                      ? t('student.dashboard.workshopException')
                      : t('student.dashboard.workshop', { defaultValue: 'Workshop' });

                const { start: startTime, end: endTime } = getSessionTimes(session);
                const sessionDateStr = formatSessionDate(session.sessionDate, i18n.language);
                const hostName = session.host
                  ? `${session.host.firstName ?? ''} ${session.host.lastName ?? ''}`.trim()
                  : null;

                return (
                  <div
                    key={session._id}
                    className={`group relative overflow-hidden rounded-3xl border-2 p-6 transition-all duration-300 shadow-sm hover:shadow-xl ${
                      isOngoing
                        ? 'border-primary/30 bg-primary/5'
                        : 'border-border bg-card hover:border-primary/20'
                    }`}
                  >
                    {isOngoing && (
                      <div className="absolute top-0 right-0 p-4">
                        <span className="flex items-center gap-1.5 rounded-full bg-red-500/10 px-2.5 py-1 text-[10px] font-black  text-red-600 border border-red-500/20 shadow-sm animate-pulse">
                          <span className="h-1.5 w-1.5 rounded-full bg-red-600" />
                          {t('common.live', { defaultValue: 'Live Now' })}
                        </span>
                      </div>
                    )}

                    <div className="flex flex-col md:flex-row gap-6">
                      <div
                        className={`flex h-16 w-16 shrink-0 items-center justify-center rounded-2xl transition-all duration-300 group-hover:scale-110 shadow-inner ${
                          isOngoing ? 'bg-primary/20' : 'bg-muted'
                        }`}
                      >
                        {session.type === 'workshop' ? (
                          <Zap
                            className={`h-8 w-8 ${isOngoing ? 'text-primary' : 'text-muted-foreground'}`}
                          />
                        ) : (
                          <BookOpen
                            className={`h-8 w-8 ${isOngoing ? 'text-primary' : 'text-muted-foreground'}`}
                          />
                        )}
                      </div>

                      <div className="flex-1 space-y-4 min-w-0 text-start">
                        <div className="space-y-1">
                          <div className="flex items-center gap-2">
                            <span className="rounded-md bg-primary/10 px-2 py-0.5 text-[10px] font-black  text-primary border border-primary/20">
                              {t(`student.dashboard.${session.type}`)}
                            </span>
                          </div>
                          <h3 className="text-xl font-black tracking-tight truncate leading-tight transition-colors group-hover:text-primary">
                            {groupNames}
                          </h3>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-4">
                          <div className="flex items-center gap-2.5 text-sm text-muted-foreground font-medium">
                            <Clock className="h-4 w-4 text-primary/60" />
                            <span>
                              {startTime}
                              {endTime ? ` — ${endTime}` : ''}
                            </span>
                          </div>
                          <div className="flex items-center gap-2.5 text-sm text-muted-foreground font-medium">
                            <Calendar className="h-4 w-4 text-primary/60" />
                            <span>{sessionDateStr}</span>
                          </div>
                          {hostName && (
                            <div className="flex items-center gap-2.5 text-sm text-muted-foreground font-medium">
                              <User className="h-4 w-4 text-primary/60" />
                              <span className="truncate">{hostName}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-2.5 text-sm font-bold tracking-wide">
                            {isOnline ? (
                              <div className="flex items-center gap-2 text-indigo-600">
                                <Wifi className="h-4 w-4" />
                                <span>{t('student.dashboard.online')}</span>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <WifiOff className="h-4 w-4" />
                                <span>{t('student.dashboard.offline')}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col justify-center items-center md:items-end gap-3 min-w-[140px]">
                        {hasJoinUrl ? (
                          <a
                            href={session.joinUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex w-full items-center justify-center gap-2 rounded-2xl bg-primary px-6 py-3 text-sm font-black text-primary-foreground shadow-xl shadow-primary/30 transition-all hover:scale-105 hover:bg-primary/90 active:scale-95"
                          >
                            <Video className="h-4 w-4" />
                            {t('student.dashboard.joinMeeting')}
                            <ExternalLink className="h-3.5 w-3.5 opacity-50" />
                          </a>
                        ) : isOnline ? (
                          <div className="flex w-full items-center justify-center gap-2 rounded-2xl bg-indigo-500/10 px-6 py-3 text-sm font-black text-indigo-600 border border-indigo-500/20">
                            <Wifi className="h-4 w-4 animate-pulse" />
                            {t('student.dashboard.onlineSession')}
                          </div>
                        ) : null}

                        <button className="p-2 rounded-lg hover:bg-muted text-muted-foreground transition-colors md:hidden">
                          <MoreVertical className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="flex flex-col items-center justify-center p-12 rounded-3xl border border-dashed bg-muted/30 space-y-4">
                <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                  <CalendarClock className="h-8 w-8 text-muted-foreground/40" />
                </div>
                <div className="text-center space-y-1">
                  <p className="font-bold text-muted-foreground">
                    {t('student.dashboard.noTodaySessions')}
                  </p>
                  <p className="text-xs text-muted-foreground/60">
                    {t('common.checkBackLater', {
                      defaultValue: 'Check back later for newly started sessions.',
                    })}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Next Sessions Card(s) ───────────────────────────────────────── */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 px-1 mb-2">
            <CalendarClock className="h-4 w-4 text-primary" />
            <h2 className="text-base font-black tracking-tight">
              {t('student.dashboard.nextSession')}
            </h2>
          </div>

          {upcomingSessions.length > 0 ? (
            <div className="space-y-3">
              {upcomingSessions.map((session: any, idx: number) => {
                const rawName: string =
                  session.groupName ||
                  session.sessionFormula ||
                  session.groups
                    ?.map((g: any) => g?.name)
                    .filter(Boolean)
                    .join(', ') ||
                  '—';
                const parts = rawName
                  .split('|')
                  .map((p: string) => p.trim())
                  .filter(Boolean);
                const cycleBadge = parts.length >= 2 ? parts[0] : null;
                const trackName = parts.length >= 2 ? parts[1] : rawName;

                const sessionDateStr = session.sessionDate
                  ? new Date(session.sessionDate).toLocaleDateString(
                      i18n.language === 'ar' ? 'ar-EG' : 'en-US',
                      { weekday: 'short', month: 'short', day: 'numeric' }
                    )
                  : session.scheduledTime?.scheduledStartDate
                    ? new Date(session.scheduledTime.scheduledStartDate).toLocaleDateString(
                        i18n.language === 'ar' ? 'ar-EG' : 'en-US',
                        { weekday: 'short', month: 'short', day: 'numeric' }
                      )
                    : null;

                const branchName = session.branch
                  ? isRtl
                    ? (session.branch.nameAr ?? session.branch.nameEn)
                    : (session.branch.nameEn ?? session.branch.nameAr)
                  : null;

                const { start: startTime, end: endTime } = getSessionTimes(session);
                const hostName = session.host
                  ? `${session.host.firstName ?? ''} ${session.host.lastName ?? ''}`.trim()
                  : null;

                return (
                  <div
                    key={`${session._id}-${idx}`}
                    className="relative rounded-2xl border border-border bg-card overflow-hidden transition-all duration-300 hover:shadow-md hover:border-primary/20 hover:-translate-y-0.5"
                  >
                    {/* left accent stripe */}
                    <div className="absolute inset-y-0 left-0 w-1 bg-primary rounded-l-2xl" />

                    <div className="pl-5 pr-4 py-4 space-y-3">
                      {/* badges row */}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {cycleBadge && (
                          <span className="px-2 py-0.5 rounded-md bg-primary text-primary-foreground text-[10px] font-black tracking-wide">
                            {cycleBadge}
                          </span>
                        )}
                        {session.sessionNumber != null && (
                          <span className="px-2 py-0.5 rounded-md bg-muted text-muted-foreground text-[10px] font-black border border-border">
                            #{session.sessionNumber}
                          </span>
                        )}
                      </div>

                      {/* track name */}
                      <h3 className="text-sm font-black leading-tight tracking-tight text-foreground">
                        {trackName}
                      </h3>

                      {/* Extra details chips (Date, Time, Instructor, Branch) */}
                      <div className="flex flex-wrap items-center gap-y-2 gap-x-4">
                        {sessionDateStr && (
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                            <Calendar className="h-3.5 w-3.5 text-primary/70 shrink-0" />
                            {sessionDateStr}
                          </span>
                        )}
                        {startTime && (
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                            <Clock className="h-3.5 w-3.5 text-primary/70 shrink-0" />
                            {startTime} {endTime ? `- ${endTime}` : ''}
                          </span>
                        )}
                        {hostName && (
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                            <User className="h-3.5 w-3.5 text-primary/70 shrink-0" />
                            {hostName}
                          </span>
                        )}
                        {session.deliveryMode === 'online' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold text-indigo-600 dark:text-indigo-400">
                            <Wifi className="h-3.5 w-3.5 shrink-0" />
                            {t('student.dashboard.online', { defaultValue: 'Online' })}
                          </span>
                        ) : branchName ? (
                          <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
                            <MapPin className="h-3.5 w-3.5 text-primary/70 shrink-0" />
                            {branchName}
                          </span>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-10 rounded-2xl border border-dashed border-border bg-muted/20 space-y-2">
              <Calendar className="h-8 w-8 text-muted-foreground/20" />
              <p className="text-sm font-bold text-muted-foreground">
                {t('student.dashboard.noUpcoming')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardPage;
